import React, {useState} from "react";
import PropTypes from "prop-types";
import {isValid} from "date-fns";

import {FILTER_KEYS, getInitialRule, getRuleMeaning, validateRule} from "../../../../services/filterRules";
import {DateFormatter, DateParser} from '../../../../services/date';
import {ResponsiveDialog} from "../../../../components/ResponsiveDialog";

import {
    Box,
    FormControlLabel,
    Grid,
    Switch,
    TextField,
    Typography,
} from "@mui/material";

import {DatePicker} from "@mui/x-date-pickers";

const RuleForm = props => {
    const {attributes, hasRule, onClose, onSave} = props;
    const [isSaving, setIsSaving] = React.useState(false);

    const {
        ruleType,
        temporalRuleType,
        valueAttribute,
        valueLabel,
        defaultValue,
        hasBooleanValue,
        defaultPeriod
    } = attributes;

    // Indicates if the user can crate default rules
    const allowValue = ruleType > 0;

    // Indicates if the user can crate temporal rules
    const allowTemporal = temporalRuleType > 0;

    // Indicates if the user can ONLY crate temporal rules
    const onlyTemporal = allowTemporal && (hasRule || !allowValue);

    // Initialize the rule object
    const initialPeriod = Array.isArray(defaultPeriod) && defaultPeriod.length === 2 ? defaultPeriod : [null, null];
    const initialRule = getInitialRule({
        [FILTER_KEYS.ruleType]: onlyTemporal ? temporalRuleType : ruleType,
        [valueAttribute]: (defaultValue !== null && defaultValue !== undefined) ? defaultValue : null,
        [FILTER_KEYS.startDay]: initialPeriod[0],
        [FILTER_KEYS.endDay]: initialPeriod[1],
    });

    const [rule, setRule] = useState(initialRule);
    const [isPickerPristine, setPickerPristine] = useState({[FILTER_KEYS.startDay]: true, [FILTER_KEYS.endDay]: true});

    const isTemporal = rule[FILTER_KEYS.ruleType] === temporalRuleType;

    const {valueErrors, periodErrors} = validateRule(rule);
    const hasValueErrors = valueErrors.length > 0;
    const hasPeriodErrors = periodErrors.length > 0;

    const enableSave = !(allowValue && hasValueErrors) && !(allowTemporal && hasPeriodErrors);

    const pickerStartValue = DateParser.safeParseISO(rule[FILTER_KEYS.startDay], null);
    const pickerEndValue = DateParser.safeParseISO(rule[FILTER_KEYS.endDay], null);
    const pickerContainsError = hasPeriodErrors && !isPickerPristine[FILTER_KEYS.startDay] && !isPickerPristine[FILTER_KEYS.endDay];

    const periodStartLabel = "Data inizio";
    const periodEndLabel = "Data fine";
    const temporalRuleLabel = "Filtro a validità temporanea";
    const meaningLabel = "Significato del filtro";

    const handleValueChange = (event) => {
        const v = parseInt(event.target.value);
        const newRule = Object.assign({}, rule, {[valueAttribute]: v});
        setRule(newRule);
    };

    const handlePeriodChange = (key) => (newValue) => {
        if (isValid(newValue)) {
            const newAttributes = {
                [key]: DateFormatter.onlyDateISOString(newValue, null)
            };
            setRule(Object.assign({}, rule, newAttributes));
            if (isPickerPristine[key]) {
                setPickerPristine(Object.assign({}, isPickerPristine, {[key]: false}))
            }
        } else {
            console.error("Date picker returns an invalid date");
        }
    };

    const handleIsTemporalChange = () => {
        const isTemporalUpdate = !isTemporal;
        let newAttributes;
        if (isTemporalUpdate) {
            newAttributes = {
                [FILTER_KEYS.ruleType]: temporalRuleType
            }
        } else {
            newAttributes = {
                [FILTER_KEYS.ruleType]: ruleType,
                [FILTER_KEYS.startDay]: null,
                [FILTER_KEYS.endDay]: null,
            }
        }

        const newRule = Object.assign({}, rule, newAttributes);
        setRule(newRule);
    };

    const handleSave = async (event) => {
        event.preventDefault();
        if (enableSave) {
            setIsSaving(true);
            await onSave(rule, isTemporal);
        }
    };

    const ruleMeaningFormatter = (value) => (
        <Box component="span" sx={{color: "primary.main"}}>
            {value}
        </Box>
    );

    return (
        <ResponsiveDialog title="Nuovo filtro"
                          isOpen={true}
                          isLoading={isSaving}
                          showApply={true}
                          applyLabel="Salva"
                          enableApply={enableSave}
                          onApply={handleSave}
                          onClose={onClose}>
            <Box sx={{pt: 1}}>
                <Grid container spacing={3}>
                {allowValue && !hasBooleanValue &&
                <Grid item xs={12}>
                    <TextField
                        type="number"
                        label={valueLabel}
                        value={rule[valueAttribute]}
                        onChange={handleValueChange}
                        error={hasValueErrors}
                        helperText={hasValueErrors ? valueErrors.join(', ') : null}
                        fullWidth={true}
                    />
                </Grid>
                }

                {allowTemporal &&
                <React.Fragment>
                    {!onlyTemporal &&
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isTemporal}
                                    onChange={handleIsTemporalChange}
                                    name="temporalSwitch"
                                    color="primary"
                                />
                            }
                            label={temporalRuleLabel}/>
                    </Grid>
                    }

                    {isTemporal &&
                    <React.Fragment>
                        <Grid item xs={12} md={6}>
                            <DatePicker disablePast
                                        label={periodStartLabel}
                                        value={pickerStartValue}
                                        onChange={handlePeriodChange(FILTER_KEYS.startDay)}
                                        disableHighlightToday
                                        closeOnSelect
                                        showDaysOutsideCurrentMonth
                                        slotProps={{
                                            textField: {
                                                error: pickerContainsError,
                                                fullWidth: true
                                            }
                                        }}/>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <DatePicker disablePast
                                        minDate={pickerStartValue}
                                        defaultCalendarMonth={pickerStartValue}
                                        label={periodEndLabel}
                                        value={pickerEndValue}
                                        onChange={handlePeriodChange(FILTER_KEYS.endDay)}
                                        closeOnSelect
                                        disableHighlightToday
                                        showDaysOutsideCurrentMonth
                                        slotProps={{
                                            textField: {
                                                error: pickerContainsError,
                                                fullWidth: true
                                            }
                                        }}/>
                        </Grid>

                        {pickerContainsError &&
                        <Grid item xs={12}>
                            <Typography variant="caption"
                                        display="block"
                                        color="error">
                                {periodErrors.join(', ')}
                            </Typography>
                        </Grid>
                        }
                    </React.Fragment>
                    }
                </React.Fragment>
                }

                <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                        {meaningLabel}
                    </Typography>
                    <div>
                        {
                            getRuleMeaning(rule, ruleMeaningFormatter).map((p, i) => (
                                    <React.Fragment key={i}>{p}</React.Fragment>
                                )
                            )
                        }
                    </div>
                </Grid>
            </Grid>
            </Box>
        </ResponsiveDialog>
    )
};

RuleForm.propTypes = {
    attributes: PropTypes.object.isRequired,
    hasRule: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired
};

export {RuleForm}